import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Icon } from 'assets/icons';
import { SOCIAL_FORMAT_LABELS } from '../../config/job';
import { ReviewActions } from '../actions';
import styles from '../view.module.css';

const CAPTION_CHANNELS = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' }
];

const buildDraft = (output = {}) => {
  const { captions = {} } = output;
  return {
    linkedin: captions.linkedin || '',
    instagram: captions.instagram || '',
    tiktok: captions.tiktok || ''
  };
};

const CopyButton = ({ value = '', label = 'caption' }) => {
  const handleCopy = async () => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error('Unable to copy. Select the text and copy it by hand.');
    }
  };

  return (
    <button type="button" className={styles.copyBtn} onClick={handleCopy} disabled={!value}>
      <Icon name="copy" width={14} height={14} />
      <span>Copy</span>
    </button>
  );
};

const CarouselPreview = ({ content = {} }) => {
  const { slides = [], slide_count } = content;

  return (
    <div className={styles.slideGrid}>
      <p className={styles.previewMeta}>{slide_count || slides.length} slides</p>
      {slides.map((slide, index) => (
        <div key={slide.slide_number || index} className={styles.slideCard}>
          <div className={styles.slideHead}>
            <span className={styles.slideNumber}>Slide {slide.slide_number || index + 1}</span>
            {slide.type && <span className={styles.slideType}>{slide.type}</span>}
          </div>
          {slide.heading && <p className={styles.slideHeading}>{slide.heading}</p>}
          {slide.body && <p className={styles.slideBody}>{slide.body}</p>}
          {slide.visual_note && (
            <p className={styles.visualNote}>
              <span className={styles.visualNoteLabel}>Visual</span>
              {slide.visual_note}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const QuoteCardPreview = ({ content = {} }) => (
  <div className={styles.quoteCard}>
    <p className={styles.quoteMark}>&ldquo;</p>
    <p className={styles.quoteText}>{content.quote}</p>
    {content.attribution && <p className={styles.quoteAttribution}>— {content.attribution}</p>}
    {content.visual_note && (
      <p className={styles.visualNote}>
        <span className={styles.visualNoteLabel}>Visual</span>
        {content.visual_note}
      </p>
    )}
  </div>
);

const DidYouKnowPreview = ({ content = {} }) => (
  <div className={styles.statCard}>
    <p className={styles.statValue}>{content.stat}</p>
    {content.context && <p className={styles.statContext}>{content.context}</p>}
    <p className={styles.statSource}>Source: {content.source || 'Not cited'}</p>
    {content.visual_note && (
      <p className={styles.visualNote}>
        <span className={styles.visualNoteLabel}>Visual</span>
        {content.visual_note}
      </p>
    )}
  </div>
);

const PollPreview = ({ content = {} }) => (
  <div className={styles.pollCard}>
    <p className={styles.pollQuestion}>{content.question}</p>
    <ul className={styles.pollOptions}>
      {(content.options || []).map((option, index) => (
        <li key={index} className={styles.pollOption}>
          {option}
        </li>
      ))}
    </ul>
    {content.duration && <p className={styles.previewMeta}>Runs for {content.duration}</p>}
  </div>
);

const TalkingHeadPreview = ({ content = {} }) => (
  <div className={styles.scriptCard}>
    {content.duration && <p className={styles.previewMeta}>{content.duration}</p>}
    {content.hook && (
      <div className={styles.scriptBlock}>
        <p className={styles.scriptLabel}>Hook</p>
        <p className={styles.scriptText}>{content.hook}</p>
      </div>
    )}
    {(content.points || []).length > 0 && (
      <div className={styles.scriptBlock}>
        <p className={styles.scriptLabel}>Points</p>
        <ul className={styles.scriptList}>
          {content.points.map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </div>
    )}
    {content.cta && (
      <div className={styles.scriptBlock}>
        <p className={styles.scriptLabel}>CTA</p>
        <p className={styles.scriptText}>{content.cta}</p>
      </div>
    )}
    {content.speaking_notes && (
      <div className={styles.scriptBlock}>
        <p className={styles.scriptLabel}>Speaking notes</p>
        <p className={styles.scriptText}>{content.speaking_notes}</p>
      </div>
    )}
  </div>
);

const FORMAT_PREVIEWS = {
  carousel: CarouselPreview,
  quote_card: QuoteCardPreview,
  did_you_know: DidYouKnowPreview,
  poll: PollPreview,
  talking_head_script: TalkingHeadPreview
};

export const SocialJobReview = ({
  content_job = {},
  output = {},
  read_only = false,
  acting = null,
  onApprove = () => {},
  onSaveDraft = () => {},
  onReject = () => {}
}) => {
  const [draft, setDraft] = useState(() => buildDraft(output));

  useEffect(() => {
    setDraft(buildDraft(output));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content_job.id, content_job.current_version, content_job.edited_output]);

  const pristine_draft = useMemo(() => buildDraft(output), [output]);
  const has_changes = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(pristine_draft),
    [draft, pristine_draft]
  );

  const { format, rationale, content = {} } = output;
  const FormatPreview = FORMAT_PREVIEWS[format];

  const buildOutput = () => ({
    ...output,
    captions: {
      ...(output.captions || {}),
      ...draft
    }
  });

  const handleCaptionChange = (key, value) => {
    setDraft((previous_draft) => ({ ...previous_draft, [key]: value }));
  };

  return (
    <>
      <div className={styles.splitPane}>
        <div className={styles.splitColumn}>
          <section className={styles.card}>
            <div className={styles.formatRow}>
              <span className={styles.formatBadge}>{SOCIAL_FORMAT_LABELS[format] || format}</span>
              {rationale && <p className={styles.formatRationale}>{rationale}</p>}
            </div>
          </section>

          <section className={styles.card}>
            <p className={styles.previewLabel}>Post preview</p>
            {FormatPreview ? (
              <FormatPreview content={content} />
            ) : (
              <pre className={styles.rawOutput}>{JSON.stringify(content, null, 2)}</pre>
            )}
          </section>
        </div>

        <div className={styles.splitColumn}>
          {CAPTION_CHANNELS.map(({ key, label }) => (
            <section key={key} className={styles.card}>
              <div className={styles.cardHead}>
                <label className={styles.fieldLabel} htmlFor={`content-job-caption-${key}`}>
                  {label} caption
                </label>
                <CopyButton value={draft[key]} label={`${label} caption`} />
              </div>
              <textarea
                id={`content-job-caption-${key}`}
                className={styles.textArea}
                value={draft[key]}
                onChange={(e) => handleCaptionChange(key, e.target.value)}
                disabled={read_only}
                placeholder={`${label} caption`}
                rows={5}
              />
            </section>
          ))}
        </div>
      </div>

      {!read_only && (
        <ReviewActions
          can_save_draft={has_changes}
          acting={acting}
          onApprove={() => onApprove(has_changes ? buildOutput() : null)}
          onSaveDraft={() => onSaveDraft(buildOutput())}
          onReject={onReject}
        />
      )}
    </>
  );
};
